// Sections — the left-sidebar groupings of agents within a Company.
// PROJECT.md "Sections — organizational namespaces within a company".
//
// Personal section is computed (NOT a row): the synthetic id is the
// string "personal". When selected, the Canvas filters to agents whose
// owner_user_id matches the current user.

import { createStore } from "./store"
import { api } from "../lib/api"

export const PERSONAL_SECTION_ID = "personal"

export const sectionsStore = createStore({
  sections: [],         // [{ id, name, color, icon, visible_to_roles, agent_count, ... }]
  loaded: false,
  loadError: null,
  currentSectionId: PERSONAL_SECTION_ID, // default to Personal on load
  saving: false,
  saveError: null,
  // Per-row menu + access editor (Option A kebab UI)
  menuSectionId: null,        // id of the section whose kebab menu is open
  editingSectionId: null,     // id of the section in the access editor
  editError: null,
})

export async function loadSections(paths) {
  try {
    const { sections } = await api.get(paths.sections)
    sectionsStore.setState((s) => {
      // First-load default: if at least one named section exists and the
      // user hasn't picked anything yet, land on the first section. This
      // is friendlier than dropping into Personal (which is empty for
      // most users on day one).
      const current = s.currentSectionId
      const stillDefault = current === PERSONAL_SECTION_ID && !s.loaded
      const next = stillDefault && sections.length > 0 ? sections[0].id : current
      return { ...s, sections, loaded: true, loadError: null, currentSectionId: next }
    })
  } catch (e) {
    sectionsStore.setState({ loadError: e.message, loaded: true })
  }
}

export async function createSection(paths, attrs) {
  sectionsStore.setState({ saving: true, saveError: null })
  try {
    const { section } = await api.post(paths.sections, { section: attrs })
    sectionsStore.setState((s) => ({
      ...s,
      sections: [...s.sections, section].sort((a, b) => a.name.localeCompare(b.name)),
      currentSectionId: section.id,
      saving: false,
    }))
    return section
  } catch (e) {
    sectionsStore.setState({ saving: false, saveError: e.message })
    throw e
  }
}

export function selectSection(id) {
  sectionsStore.setState({ currentSectionId: id })
}

export function openSectionMenu(id) {
  sectionsStore.setState({ menuSectionId: id })
}

export function closeSectionMenu() {
  sectionsStore.setState({ menuSectionId: null })
}

export function openSectionEditor(id) {
  sectionsStore.setState({ editingSectionId: id, menuSectionId: null, editError: null })
}

export function closeSectionEditor() {
  sectionsStore.setState({ editingSectionId: null, editError: null })
}

export async function updateSection(paths, id, attrs) {
  sectionsStore.setState({ saving: true, editError: null })
  try {
    const { section } = await api.put(`${paths.sections}/${id}`, { section: attrs })
    sectionsStore.setState((s) => ({
      ...s,
      sections: s.sections.map((row) => (row.id === id ? section : row))
                          .sort((a, b) => a.name.localeCompare(b.name)),
      saving: false,
      editingSectionId: null,
    }))
    return section
  } catch (e) {
    sectionsStore.setState({ saving: false, editError: e.message })
    throw e
  }
}
